import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in usePaymentIntents, so applying both narrows rather than
// resets.
//
// ═══ EVERY FILTER HERE IS CLIENT-SIDE. THAT IS A SCHEMA FACT, NOT A CHOICE ═══
//
// `PaymentIntentsFilterInput` declares exactly ONE field:
//
//     input PaymentIntentsFilterInput { customerId: String }
//
// (BILLING/APIS/flexinet-stripe-api/src/appsync-api/src/schema/features/paymentIntents.graphql)
//
// `customerId` is not offered in the drawer because it is already the table's
// scope — the customer EditPage tab passes it as a prop, and on the index page
// the keyword search covers it.
//
// That leaves `status`, which the API CANNOT filter. Adding `status` to the
// filter variable would not error: AppSync silently DROPS input fields the
// schema does not declare, so the request would succeed and return the
// unfiltered list while the UI showed an active filter badge. A filter that
// looks applied and is not is worse than no filter, so `status` is matched
// client-side over the loaded window and the drawer says so in as many words.
//
// The honest scope is therefore "the pages loaded so far", which is the same
// scope the keyword and the sort work over. The footer states a LOADED count
// and never a dataset total for exactly this reason.
export type PaymentIntentsFilters = Record<string, string>;

let globalFilters: PaymentIntentsFilters = {};
const filterListeners: Set<(filters: PaymentIntentsFilters) => void> = new Set();

const notifyFilterChange = (filters: PaymentIntentsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetPaymentIntentsFilter = () => notifyFilterChange({});

export const usePaymentIntentsFilter = () => {
    const [filters, setFilters] = useState<PaymentIntentsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: PaymentIntentsFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
